import React, { useCallback, useRef, useState } from 'react';
import { Platform, View, StyleSheet, useWindowDimensions } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { useResponsive } from '@/hooks/common/useResponsive';
import { AdminSidebar } from '@/components/common/admin/desktop/AdminSidebar';
import { ADMIN_PATH_TO_SCREEN } from '@/utils/admin/navigation';
import { ADMIN_COLORS } from '@/constants/admin';
import { IBulletinTableRow } from '@/types/admin/bulletins';
import { ICommunityResource } from '@/types/admin/resources';
import { IUserManagementTableRow } from '@/types/admin/userManagement';
import { ResidentList } from '@/screens/admin/resident/ResidentList';
import { ResidentDetails } from '@/screens/admin/resident/ResidentDetails';
import { TransactionHistoryScreen } from '@/screens/admin/resident/TransactionHistoryScreen';
import { EventsScreen } from '@/screens/admin/events/EventsScreen';
import { BulletinsScreen } from '@/screens/admin/bulletins/BulletinsScreen';
import { MessagesScreen } from '@/screens/admin/messages/MessagesScreen';
import { ResourcesScreen } from '@/screens/admin/resources/ResourcesScreen';
import { CreateResourceScreen } from '@/screens/admin/resources/CreateResourceScreen';
import { EditResourceScreen } from '@/screens/admin/resources/EditResourceScreen';
import { TasksScreen } from '@/screens/admin/tasks/TasksScreen';
import { FundingScreen } from '@/screens/admin/funding/FundingScreen';
import { OrganizationsScreen } from '@/screens/admin/organizations/OrganizationsScreen';
import { UserManagementScreen } from '@/screens/admin/userManagement/UserManagementScreen';
import { PropertiesScreen } from '@/screens/admin/properties/PropertiesScreen';
import { FeatureFlagsScreen } from '@/screens/admin/featureFlags/FeatureFlagsScreen';
import { ChatScreen } from '@/screens/admin/messages/ChatScreen';
import { CreateEventScreen } from '@/screens/admin/events/CreateEventScreen';
import { EditEventScreen } from '@/screens/admin/events/EditEventScreen';
import { CreateBulletinScreen } from '@/screens/admin/bulletins/CreateBulletinScreen';
import { EditBulletinScreen } from '@/screens/admin/bulletins/EditBulletinScreen';
import { CreateUserScreen } from '@/screens/admin/userManagement/CreateUserScreen';
import { EditUserScreen } from '@/screens/admin/userManagement/EditUserScreen';
import { CreateTaskScreen } from '@/screens/admin/tasks/CreateTaskScreen';
import { EditTaskScreen } from '@/screens/admin/tasks/EditTaskScreen';
import { CreateMessageScreen } from '@/screens/admin/messages/CreateMessageScreen';
import { EditMessageScreen } from '@/screens/admin/messages/EditMessageScreen';
import { TagManagementScreen } from '@/screens/admin/corkBoard/TagManagementScreen';
import { ThreadModerationScreen } from '@/screens/admin/corkBoard/ThreadModerationScreen';
import { ReportReviewScreen } from '@/screens/admin/corkBoard/ReportReviewScreen';

export type AdminStackParamList = {
  ResidentList: undefined;
  ResidentDetails: { accountId: number; leaseId: number; mode?: 'account' | 'resident' };
  TransactionHistory: { accountId: number; leaseId: number; residentName?: string };
  Events: undefined;
  CreateEvent: undefined;
  EditEvent: { eventId: number };
  Bulletins: undefined;
  CreateBulletin: undefined;
  EditBulletin: { bulletin: IBulletinTableRow };
  Messages: undefined;
  CreateMessage: undefined;
  EditMessage: { conversationId: number };
  Chat: { conversationId: number };
  Resources: undefined;
  CreateResource: { highestPosition?: number };
  EditResource: { resource: ICommunityResource };
  Tasks: undefined;
  CreateTask: undefined;
  EditTask: { taskId: number };
  Funding: undefined;
  Organizations: undefined;
  UserManagement: undefined;
  CreateUser: undefined;
  EditUser: { user: IUserManagementTableRow };
  Properties: undefined;
  FeatureFlags: undefined;
  TagManagement: undefined;
  ThreadModeration: undefined;
  ReportReview: undefined;
};

const Stack = createStackNavigator<AdminStackParamList>();

export const AdminNavigator: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  const [currentRoute, setCurrentRoute] = useState<keyof AdminStackParamList>('ResidentList');
  const adminNavRef = useRef<any>(null);

  const { height: windowHeight } = useWindowDimensions();
  const wrapperHeight = Platform.OS === 'web' ? { height: windowHeight } : undefined;

  const handleSidebarNavigate = useCallback((path: string) => {
    const screenName = ADMIN_PATH_TO_SCREEN[path];
    if (screenName && adminNavRef.current) {
      adminNavRef.current.navigate(screenName);
    }
  }, []);

  const navigator = (
    <Stack.Navigator
      initialRouteName="ResidentList"
      screenListeners={({ navigation, route }) => ({
        focus: () => {
          setCurrentRoute(route.name as keyof AdminStackParamList);
          adminNavRef.current = navigation;
        },
      })}
      screenOptions={{
        headerShown: false,
        ...(Platform.OS === 'web'
          ? { animationEnabled: false as const }
          : {
              cardStyleInterpolator: ({ current }) => ({
                cardStyle: { opacity: current.progress },
              }),
              transitionSpec: {
                open: { animation: 'timing', config: { duration: 250 } },
                close: { animation: 'timing', config: { duration: 200 } },
              },
            }),
      }}
    >
      <Stack.Screen name="ResidentList" component={ResidentList} />
      <Stack.Screen name="ResidentDetails" component={ResidentDetails} />
      <Stack.Screen name="TransactionHistory" component={TransactionHistoryScreen} />
      <Stack.Screen name="Events" component={EventsScreen} />
      <Stack.Screen name="CreateEvent" component={CreateEventScreen} />
      <Stack.Screen name="EditEvent" component={EditEventScreen} />
      <Stack.Screen name="Bulletins" component={BulletinsScreen} />
      <Stack.Screen name="CreateBulletin" component={CreateBulletinScreen} />
      <Stack.Screen name="EditBulletin" component={EditBulletinScreen} />
      <Stack.Screen name="Messages" component={MessagesScreen} />
      <Stack.Screen name="CreateMessage" component={CreateMessageScreen} />
      <Stack.Screen name="EditMessage" component={EditMessageScreen} />
      <Stack.Screen name="Chat" component={ChatScreen} />
      <Stack.Screen name="Resources" component={ResourcesScreen} />
      <Stack.Screen name="CreateResource" component={CreateResourceScreen} />
      <Stack.Screen name="EditResource" component={EditResourceScreen} />
      <Stack.Screen name="Tasks" component={TasksScreen} />
      <Stack.Screen name="CreateTask" component={CreateTaskScreen} />
      <Stack.Screen name="EditTask" component={EditTaskScreen} />
      <Stack.Screen name="Funding" component={FundingScreen} />
      <Stack.Screen name="Organizations" component={OrganizationsScreen} />
      <Stack.Screen name="UserManagement" component={UserManagementScreen} />
      <Stack.Screen name="CreateUser" component={CreateUserScreen} />
      <Stack.Screen name="EditUser" component={EditUserScreen} />
      <Stack.Screen name="Properties" component={PropertiesScreen} />
      <Stack.Screen name="FeatureFlags" component={FeatureFlagsScreen} />
      <Stack.Screen name="TagManagement" component={TagManagementScreen} />
      <Stack.Screen name="ThreadModeration" component={ThreadModerationScreen} />
      <Stack.Screen name="ReportReview" component={ReportReviewScreen} />
    </Stack.Navigator>
  );

  if (isDesktop) {
    return (
      <View style={[desktopStyles.wrapper, wrapperHeight]}>
        <View style={[desktopStyles.sidebarContainer, wrapperHeight]}>
          <AdminSidebar
            routeName={currentRoute}
            onNavigate={handleSidebarNavigate}
          />
        </View>
        <View style={desktopStyles.navigatorContainer}>
          {navigator}
        </View>
      </View>
    );
  }

  return navigator;
};

const desktopStyles = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#171717',
  },
  sidebarContainer: {
    width: 280,
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    flexShrink: 0,
    borderTopRightRadius: 30,
  },
  navigatorContainer: {
    flex: 1,
  },
});
